import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, Loader2, ThumbsUp, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Public survey form — no auth required. Submissions land in the admin
// moderation queue (source: user_pending, moderation: pending). The
// backend enforces a per-IP rate limit + honeypot check.
function StarRow({ label, value, onChange, testid, required }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-sm text-stone-700">
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </span>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-0.5 transition-transform hover:scale-110"
            data-testid={`${testid}-${n}`}
            aria-label={`${label} ${n} stars`}
          >
            <Star
              className={`h-6 w-6 ${
                n <= (value || 0)
                  ? "fill-amber-400 text-amber-400"
                  : "text-stone-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Survey() {
  const { degreeId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get("t") || null;

  const [degree, setDegree] = useState(null);
  const [inviteLabel, setInviteLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    overall_rating: 0,
    teaching_rating: 0,
    campus_rating: 0,
    value_rating: 0,
    would_recommend: null,
    review: "",
    reviewer_display_name: "",
    graduation_year: "",
    honeypot: "",
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await api.get(`/degrees/${degreeId}`);
        if (!cancelled) setDegree(d.data);
        if (token) {
          try {
            const inv = await api.get(`/survey/invite/${token}`);
            if (!cancelled) setInviteLabel(inv.data?.label || null);
          } catch (_) {
            // Invalid token is non-fatal — the form still submits.
          }
        }
      } catch (_) {
        if (!cancelled) setDegree(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [degreeId, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.overall_rating) {
      toast.error("Please rate the overall experience.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        degree_id: degreeId,
        overall_rating: form.overall_rating,
        teaching_rating: form.teaching_rating || null,
        campus_rating: form.campus_rating || null,
        value_rating: form.value_rating || null,
        would_recommend: form.would_recommend,
        review: form.review || null,
        reviewer_display_name: form.reviewer_display_name || null,
        graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
        invite_token: token,
        honeypot: form.honeypot,
      };
      await api.post("/survey/submit", payload);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit — please try again in a bit.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm">
        Loading survey…
      </div>
    );
  }

  if (!degree) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="p-10 border border-dashed border-stone-300 rounded-lg text-stone-600">
          <div className="label-uppercase text-stone-500">Course not found</div>
          <p className="mt-3">
            We couldn't find that degree. It may have moved.
          </p>
          <Link to="/" className="mt-4 inline-flex items-center gap-1 text-sm text-[#4C1D95] hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="survey-thank-you">
        <div className="p-10 border border-emerald-200 bg-emerald-50 rounded-lg">
          <CheckCircle2 className="h-10 w-10 text-emerald-700" />
          <h1 className="mt-4 font-editorial text-3xl text-stone-900">Thank you.</h1>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Your review has been received and will appear on the course page
            once one of our editors approves it — usually within a couple of
            working days. We don't publish personal details, and we won't
            contact you unless you asked us to.
          </p>
          <Link
            to={`/degree/${degreeId}`}
            className="mt-6 inline-flex items-center gap-1 text-sm text-[#4C1D95] hover:underline"
            data-testid="survey-back-to-course"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to the course
          </Link>
        </div>
      </div>
    );
  }

  const degreeTitle = degree.official_title || degree.title || "this course";
  const uniName = degree.university_name || degree.university?.name || "";

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="survey-page">
      <div className="label-uppercase text-stone-500">
        Student survey
        {inviteLabel && <span className="ml-2 text-[#4C1D95]">· {inviteLabel}</span>}
      </div>
      <h1 className="mt-1 font-editorial text-3xl text-stone-900" data-testid="survey-course-title">
        How was studying {degreeTitle}?
      </h1>
      {uniName && (
        <p className="mt-1 text-sm text-stone-600" data-testid="survey-uni-name">
          at {uniName}
        </p>
      )}
      <p className="mt-4 text-sm text-stone-700 leading-relaxed">
        We're building an honest, student-led view of universities worldwide.
        Your review will help someone else make a life-changing decision.
        Anonymous by default — takes about a minute.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4 border-t border-stone-200 pt-6"
            data-testid="survey-form">
        <StarRow
          label="Overall experience"
          required
          value={form.overall_rating}
          onChange={(n) => setForm((s) => ({ ...s, overall_rating: n }))}
          testid="survey-stars-overall"
        />
        <StarRow
          label="Teaching quality"
          value={form.teaching_rating}
          onChange={(n) => setForm((s) => ({ ...s, teaching_rating: n }))}
          testid="survey-stars-teaching"
        />
        <StarRow
          label="Value for money"
          value={form.value_rating}
          onChange={(n) => setForm((s) => ({ ...s, value_rating: n }))}
          testid="survey-stars-value"
        />
        <StarRow
          label="Campus & city life"
          value={form.campus_rating}
          onChange={(n) => setForm((s) => ({ ...s, campus_rating: n }))}
          testid="survey-stars-campus"
        />

        <div className="flex items-center gap-3 py-1.5">
          <span className="text-sm text-stone-700">Would you recommend it?</span>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="survey-recommend-yes"
              onClick={() => setForm((s) => ({ ...s, would_recommend: true }))}
              className={`px-3 py-1 rounded-full text-xs font-underground-sc border transition-colors ${
                form.would_recommend === true
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white border-stone-300 text-stone-700 hover:border-emerald-400"
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              data-testid="survey-recommend-no"
              onClick={() => setForm((s) => ({ ...s, would_recommend: false }))}
              className={`px-3 py-1 rounded-full text-xs font-underground-sc border transition-colors ${
                form.would_recommend === false
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"
              }`}
            >
              No
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div>
            <Label htmlFor="survey-name" className="label-uppercase">
              Display name (optional)
            </Label>
            <Input
              id="survey-name"
              data-testid="survey-name"
              className="mt-1"
              value={form.reviewer_display_name}
              onChange={(e) =>
                setForm((s) => ({ ...s, reviewer_display_name: e.target.value }))
              }
              placeholder="Alex M."
              maxLength={80}
            />
          </div>
          <div>
            <Label htmlFor="survey-year" className="label-uppercase">
              Graduation year (optional)
            </Label>
            <Input
              id="survey-year"
              data-testid="survey-year"
              className="mt-1 font-mono"
              type="number"
              min="1980"
              max="2035"
              value={form.graduation_year}
              onChange={(e) =>
                setForm((s) => ({ ...s, graduation_year: e.target.value }))
              }
              placeholder="e.g. 2025"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="survey-review" className="label-uppercase">
            Your review
          </Label>
          <Textarea
            id="survey-review"
            data-testid="survey-review"
            className="mt-1"
            rows={5}
            value={form.review}
            onChange={(e) => setForm((s) => ({ ...s, review: e.target.value }))}
            placeholder="What did you enjoy? What surprised you? What would you tell someone thinking about applying?"
            maxLength={4000}
          />
        </div>

        {/* Honeypot — invisible to humans, catches most naive bots. */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
        >
          <Label htmlFor="survey-hp">Website (leave blank)</Label>
          <Input
            id="survey-hp"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={(e) => setForm((s) => ({ ...s, honeypot: e.target.value }))}
          />
        </div>

        <div className="pt-4 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[11px] text-stone-500 leading-snug max-w-md">
            Reviews are moderated before they go public. We keep no personal
            information beyond what you type here. By submitting, you confirm
            this is your honest experience.
          </p>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="survey-submit"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <ThumbsUp className="h-4 w-4 mr-1.5" /> Submit review
              </>
            )}
          </Button>
        </div>
      </form>

      <div className="mt-8">
        <Link
          to={`/degree/${degreeId}`}
          data-testid="survey-cancel"
          className="text-xs font-underground-sc tracking-wider text-stone-500 hover:text-stone-800"
        >
          ← Back to course page
        </Link>
      </div>
    </div>
  );
}
